import { useDarkMode } from "@/hooks/useDarkMode";
import { useLoadGoogleMaps } from "@/hooks/useLoadGoogleMaps";
import { useSelectedVenue } from "@/hooks/useSelectedVenue";
import {
  hoveredVenueIdAtom,
  mobileListViewAtom,
  mapDetailOriginAtom,
  savedPlaceIdToViewAtom,
  viewModeAtom,
} from "@/state/atoms";
import {
  APIProvider,
  Map as VisGlMap,
  AdvancedMarker,
  InfoWindow,
  useMap,
} from "@vis.gl/react-google-maps";
import { googleMapId, googleMapKey } from "common/config";
import { Venue, VenuePinType } from "common/types";
import { useAtom } from "jotai";
import React, { useEffect, useMemo, useRef, useState } from "react";
import GreyPin from "@/assets/pins/grey_pin.svg";
import OrangePin from "@/assets/pins/orange_pin.svg";
import BluePin from "@/assets/pins/blue_pin.svg";
import BlackPin from "@/assets/pins/black_pin.svg";
import GreenPin from "@/assets/pins/green_pin.svg";
import PurplePin from "@/assets/pins/purple_pin.svg";
import GreyPinSelected from "@/assets/pins/grey_pin_selected.svg";
import OrangePinSelected from "@/assets/pins/orange_pin_selected.svg";
import { cn } from "@/lib/utils";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { getPinIcon } from "common/utils/maps";
import { pinTypeForVenue } from "common/utils";
import { trackEvent } from "common/analytics";
import { StandardButton } from "@/components/common/StandardButton";
import VenueCard from "@/components/VenueCard/VenueCard";
import { XIcon } from "@phosphor-icons/react";
import YouAre from "@/assets/pins/youAre.svg";
import { useGeoLocation } from "@/hooks/useGeoLocation";
import { useAuth } from "common/hooks";

// Lifts the underlying google.maps.Map instance out of the vis.gl context so the
// existing imperative pan/zoom/fitBounds logic keeps working unchanged.
function MapRefCapture({
  onMap,
}: {
  onMap: (map: google.maps.Map) => void;
}) {
  const map = useMap();
  useEffect(() => {
    if (map) onMap(map);
  }, [map, onMap]);
  return null;
}

// Stable reference so vis.gl InfoWindow never sees a changed pixelOffset between renders.
const SNIPPET_PIXEL_OFFSET: [number, number] = [0, -50];

function MapViewForNearby(props: {
  venueLocation: { latitude: number; longitude: number };
  filteredBusinesses: Venue[];
  venueId: string;
  hidden?: boolean;
}) {
  const [savedPlaceIdToView] = useAtom(savedPlaceIdToViewAtom);
  const [viewMode] = useAtom(viewModeAtom);
  const { isMapper } = useAuth();
  const isMapperViewActive = isMapper && viewMode === "mapper";

  const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);

  const [activeMarker, setActiveMarker] = useState<string | null>(null);
  const [tooltipVenue, setTooltipVenue] = useState<Venue | null>(null);
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string | null>(null);
  // Snippet (venue preview) shown when a marker is tapped on the results map.
  const [snippetOpen, setSnippetOpen] = useState<boolean>(false);
  const { selectedVenue, setSelectedVenue } = useSelectedVenue();
  const [, setMobileListView] = useAtom(mobileListViewAtom);
  const [, setDetailOrigin] = useAtom(mapDetailOriginAtom);
  const [hoveredVenueId, setHoveredVenueId] = useAtom(hoveredVenueIdAtom);
  const { isMapsLoaded } = useLoadGoogleMaps();
  const isDark = useDarkMode();
  const hasInitialFitBoundsRef = useRef(false);
  const hasInitialPanRef = useRef(false);

  const { location: userLocation } = useGeoLocation();

  const handleLoadVenue = (location: Venue) => {
    // Detail panel lives inside the list container, so we flip to it to show
    // the detail — but remember the user came from the map so "Back to all
    // results" can return them here.
    setDetailOrigin("map");
    setMobileListView(true);
    setSelectedVenue(location);
    setActiveMarker(null);
    setTooltipVenue(null);
    setHoveredMarkerId(null);
  };

  const [delayedLocations, setDelayedLocations] = useState<Venue[]>([]);

  useEffect(() => {
    if (props.filteredBusinesses?.length) {
      const finalLocations =
        props.filteredBusinesses.map((e: Venue) => {
          if (e.venuePinType) return e;
          return {
            ...e,
            venuePinType: getPinIcon(e),
          };
        }) || [];

      // Small delay to ensure map is ready
      setTimeout(() => {
        setDelayedLocations(finalLocations);
      }, 100);
    } else {
      // Clear markers when filteredBusinesses is empty
      setDelayedLocations([]);
    }
  }, [props.filteredBusinesses]);

  const locations = delayedLocations;

  // Show the venue preview snippet for a marker (no venue loading yet).
  const handleShowSnippet = (marker: string) => {
    if (marker === activeMarker && snippetOpen) {
      return;
    }

    const venue = locations.find((l) => l.id === marker);
    if (!venue) return;

    setTooltipVenue(null);
    setHoveredMarkerId(marker);
    setHoveredVenueId(marker);
    setActiveMarker(marker);
    setSnippetOpen(true);
  };

  // Close the snippet without loading a venue.
  const handleCloseSnippet = () => {
    setActiveMarker(null);
    setTooltipVenue(null);
    setHoveredMarkerId(null);
    setHoveredVenueId("");
    setSnippetOpen(false);
  };

  // Stable snippet venue + position — memoised on activeMarker so unrelated state
  // changes don't produce new object references (which would make vis.gl call
  // infoWindow.setPosition() and flicker).
  const snippetVenue = useMemo(() => {
    if (!activeMarker) return null;
    return locations.find((l) => l.id === activeMarker) || null;
  }, [activeMarker, locations]);

  const snippetPosition = useMemo(
    () =>
      snippetVenue
        ? { lat: snippetVenue.latitude, lng: snippetVenue.longitude }
        : null,
    [snippetVenue],
  );

  const handleMapInstance = React.useCallback(
    (map: google.maps.Map) => {
      setMapRef(map);
      // Center map on venue location when first loaded (fitBounds takes over
      // once results arrive).
      if (props.venueLocation && !hasInitialPanRef.current) {
        map.panTo({
          lat: props.venueLocation.latitude,
          lng: props.venueLocation.longitude,
        });
        map.setZoom(15);
        hasInitialPanRef.current = true;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [props.venueLocation?.latitude, props.venueLocation?.longitude],
  );

  const showPinIcon = (venue: Venue) => {
    // User view: Only show Orange/Grey pins
    if (!isMapperViewActive) {
      if (venue.venuePinType === VenuePinType.Grey) {
        return GreyPin;
      } else {
        return OrangePin;
      }
    }

    // Mapper view: Show all 6 pin types
    switch (venue.venuePinType) {
      case VenuePinType.Green:
        return GreenPin;
      case VenuePinType.Black:
        return BlackPin;
      case VenuePinType.Blue:
        return BluePin;
      case VenuePinType.Purple:
        return PurplePin;
      case VenuePinType.Orange:
        return OrangePin;
      case VenuePinType.Grey:
        return GreyPin;
      default:
        return GreyPin;
    }
  };

  const getSelectedPinIcon = (venue: Venue) => {
    // User view: Only show Orange/Grey selected pins
    if (!isMapperViewActive) {
      if (venue.venuePinType === VenuePinType.Grey) {
        return GreyPinSelected;
      } else {
        return OrangePinSelected;
      }
    }

    // Mapper view: Show all pin types (selected variants only for Grey/Orange)
    switch (venue.venuePinType) {
      case VenuePinType.Grey:
        return GreyPinSelected;
      case VenuePinType.Orange:
        return OrangePinSelected;
      case VenuePinType.Green:
        return GreenPin;
      case VenuePinType.Black:
        return BlackPin;
      case VenuePinType.Blue:
        return BluePin;
      case VenuePinType.Purple:
        return PurplePin;
      default:
        return GreyPinSelected;
    }
  };

  const isValidCoord = (lat: number | undefined, lng: number | undefined) =>
    lat !== undefined &&
    lng !== undefined &&
    Math.abs(lat) > 0.1 &&
    Math.abs(lng) > 0.1;

  // Great-circle distance in km between two lat/lng points (haversine).
  const distanceKm = (
    lat1: number,
    lng1: number,
    lat2: number,
    lng2: number,
  ) => {
    const toRad = (d: number) => (d * Math.PI) / 180;
    const R = 6371;
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(toRad(lat1)) *
        Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) ** 2;
    return 2 * R * Math.asin(Math.sqrt(a));
  };

  useEffect(() => {
    // Only fit bounds once on initial load — skip on subsequent filter/sort changes
    if (hasInitialFitBoundsRef.current) return;

    if (!(locations.length && mapRef && !activeMarker)) return;

    const validLocations = locations.filter((l) =>
      isValidCoord(l.latitude, l.longitude),
    );

    const vLat = props.venueLocation?.latitude;
    const vLng = props.venueLocation?.longitude;
    const hasValidVenue = isValidCoord(vLat, vLng);

    if (!validLocations.length && !hasValidVenue) return;

    // Anchor the view on the main venue when we have it, otherwise fall back to
    // the first valid nearby venue.
    const anchor = hasValidVenue
      ? { lat: vLat!, lng: vLng! }
      : { lat: validLocations[0].latitude, lng: validLocations[0].longitude };

    // The nearby list can include venues spread across the whole country (e.g.
    // brand pages with sites in Oxford, Cardiff, Norwich…). Fitting bounds over
    // all of them zooms out to show all of England. Instead, fit only to the
    // local cluster around the anchor so the initial view is meaningful — the
    // distant markers are still rendered and reachable by panning.
    const CLUSTER_RADIUS_KM = 30;
    const clustered = validLocations.filter(
      (l) =>
        distanceKm(anchor.lat, anchor.lng, l.latitude, l.longitude) <=
        CLUSTER_RADIUS_KM,
    );

    const bounds = new google.maps.LatLngBounds();
    bounds.extend(new google.maps.LatLng(anchor.lat, anchor.lng));
    clustered.forEach((l) =>
      bounds.extend(new google.maps.LatLng(l.latitude, l.longitude)),
    );

    if (bounds.isEmpty()) return;

    mapRef.fitBounds(bounds);
    google.maps.event.addListenerOnce(mapRef, "bounds_changed", () => {
      const currentZoom = mapRef.getZoom();
      if (currentZoom === undefined) return;
      // When only the anchor is in range, fitBounds collapses to a single
      // point and over-zooms — pull it back to a sensible neighbourhood zoom.
      if (clustered.length === 0) {
        mapRef.setZoom(14);
      } else if (currentZoom > 16) {
        mapRef.setZoom(16);
      }
    });
    hasInitialFitBoundsRef.current = true;
  }, [locations, props.venueLocation, mapRef, activeMarker]);

  // code to show venue marker highlighted
  useEffect(() => {
    if (selectedVenue && savedPlaceIdToView == "" && mapRef) {
      const newPosition = new google.maps.LatLng({
        lat: selectedVenue.latitude,
        lng: selectedVenue.longitude,
      });
      mapRef.panTo(newPosition);
      setActiveMarker(selectedVenue.id);
    } else {
      setActiveMarker(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVenue, mapRef]);

  // Close the preview snippet when the user drags or zooms the map.
  useEffect(() => {
    if (!mapRef) return;

    const closeSnippet = () => {
      setActiveMarker(null);
      setTooltipVenue(null);
      setHoveredMarkerId(null);
      setSnippetOpen(false);
    };

    const dragStartListener = mapRef.addListener("dragstart", closeSnippet);
    const zoomChangedListener = mapRef.addListener("zoom_changed", closeSnippet);

    return () => {
      if (dragStartListener) google.maps.event.removeListener(dragStartListener);
      if (zoomChangedListener) {
        google.maps.event.removeListener(zoomChangedListener);
      }
    };
  }, [mapRef]);

  // Escape key handler to return focus to results list
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        const mapContainer = document.querySelector(
          '[aria-label="Map view of nearby venues"]',
        );
        if (mapContainer && mapContainer.contains(document.activeElement)) {
          // Move focus to results list
          const resultsPanel = document.getElementById("venue-list-panel");
          if (resultsPanel) {
            resultsPanel.setAttribute("tabindex", "-1");
            resultsPanel.focus();
            resultsPanel.addEventListener(
              "blur",
              () => {
                resultsPanel.removeAttribute("tabindex");
              },
              { once: true },
            );
          }
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Stable reference so the map doesn't re-pan on every render.
  // Only set when coordinates are valid — prevents the map snapping to 0,0 (off
  // the coast of Africa) when venueLocation hasn't been populated yet.
  const mapCenterValue = useMemo(() => {
    const lat = props.venueLocation?.latitude;
    const lng = props.venueLocation?.longitude;
    return isValidCoord(lat, lng) ? { lat: lat!, lng: lng! } : undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.venueLocation?.latitude, props.venueLocation?.longitude]);

  // Hover tooltip target (only shown when no snippet is open).
  const tooltipDisplayVenue = snippetOpen
    ? null
    : tooltipVenue ||
      (hoveredVenueId ? locations.find((l) => l.id === hoveredVenueId) : null);

  return (
    <div
      className={cn(
        "flex-1 relative h-full rounded-[12px] overflow-clip",
        props.hidden ? "hidden" : "block",
      )}
      aria-label="Map view of nearby venues"
      role="region"
    >
      <div className="sr-only">
        Use Tab to navigate map controls. Press Escape to exit map and return to
        results list.
      </div>
      {isMapsLoaded && (
        <APIProvider
          apiKey={googleMapKey}
          libraries={["marker", "geometry"]}
          version="3.55"
        >
          <VisGlMap
            mapId={googleMapId}
            colorScheme={isDark ? "DARK" : "LIGHT"}
            style={{ width: "100%", height: "100%" }}
            defaultCenter={mapCenterValue}
            defaultZoom={15}
            gestureHandling="greedy"
            disableDefaultUI
            keyboardShortcuts={false}
            onClick={() => {
              setActiveMarker(null);
              setTooltipVenue(null);
              setHoveredMarkerId(null);
              setHoveredVenueId("");
              setSnippetOpen(false);
            }}
          >
            <MapRefCapture onMap={handleMapInstance} />

            {locations.map((location, index) => {
              const isSelected = !!(
                selectedVenue && selectedVenue.id === location.id
              );
              const isHovered =
                hoveredMarkerId === location.id ||
                hoveredVenueId === location.id ||
                activeMarker === location.id;

              const pinUrl = isSelected
                ? getSelectedPinIcon(location)
                : showPinIcon(location);
              const dim = isSelected ? 40 : isHovered ? 42 : 30;
              const markerZIndex = isSelected ? 1000 : index;

              return (
                <AdvancedMarker
                  key={location.id}
                  position={{
                    lat: location.latitude,
                    lng: location.longitude,
                  }}
                  zIndex={markerZIndex}
                  onClick={() => {
                    // place_pin_target_area -> place_pin_selected (area-map marker tap).
                    // entry_index = pin position in the results list; pin_type via the
                    // shared canonical helper. Mirrors the BasicMapView pin wiring.
                    trackEvent("place_pin_selected", {
                      brand_id: location.brandId ?? "",
                      brand_name: location.brandName ?? "",
                      business_id: location.business?.id?.toString() ?? "",
                      entry_index: index,
                      pin_type: pinTypeForVenue(location),
                      place_name: location.name ?? "",
                    });

                    if (selectedVenue) {
                      // A venue detail is already open (desktop side map) — open the
                      // clicked venue directly, no snippet.
                      handleLoadVenue(location);
                    } else {
                      // Results map — show the preview snippet first.
                      handleShowSnippet(location.id);
                    }
                  }}
                >
                  <div
                    onMouseEnter={() => {
                      if (snippetOpen) return;
                      setTooltipVenue(location);
                      setHoveredMarkerId(location.id);
                    }}
                    onMouseLeave={() => {
                      if (snippetOpen) return;
                      if (activeMarker !== location.id) {
                        setTooltipVenue(null);
                        setHoveredMarkerId(null);
                      }
                    }}
                  >
                    <img
                      src={pinUrl}
                      alt=""
                      width={dim}
                      height={dim}
                      draggable={false}
                      style={{ display: "block" }}
                    />
                    <span className="sr-only">
                      {`${location.name}, ${location.businessType}, ${location.distance} miles away`}
                    </span>
                  </div>
                </AdvancedMarker>
              );
            })}

            {userLocation && (
              <AdvancedMarker
                key="userLocation"
                position={{
                  lat: userLocation.latitude,
                  lng: userLocation.longitude,
                }}
                zIndex={10000}
              >
                <img
                  src={YouAre}
                  alt="Your location"
                  width={30}
                  height={30}
                  draggable={false}
                  style={{ display: "block", pointerEvents: "none" }}
                />
              </AdvancedMarker>
            )}

            {/* Hover tooltip — non-interactive, sits above the pin */}
            {tooltipDisplayVenue && (
              <AdvancedMarker
                key={`tooltip-${tooltipDisplayVenue.id}`}
                position={{
                  lat: tooltipDisplayVenue.latitude,
                  lng: tooltipDisplayVenue.longitude,
                }}
                zIndex={999999}
              >
                <div
                  style={{ transform: "translateY(-46px)", pointerEvents: "none" }}
                >
                  <div className="bg-bg-brand-secondary-default text-fg-neutral-strong px-4 py-2 rounded-lg shadow-lg w-max max-w-[400px]">
                    <div className="font-medium label-m-medium whitespace-nowrap">
                      {tooltipDisplayVenue.name}
                    </div>
                  </div>
                </div>
              </AdvancedMarker>
            )}

            {/* Desktop snippet (md and up) anchored at the venue position */}
            {!selectedVenue && snippetOpen && snippetVenue && snippetPosition && (
              <InfoWindow
                position={snippetPosition}
                headerDisabled
                pixelOffset={SNIPPET_PIXEL_OFFSET}
                className="adv-map-snippet"
              >
                <div className="hidden md:block w-[300px]">
                  <div className="map-snippet-wrapper relative bg-bg-brand-secondary-default rounded-lg shadow-md w-full">
                    <style>{`
                      .map-snippet-wrapper button.bg-opacity-30 {
                        right: 52px !important;
                      }`}</style>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCloseSnippet();
                      }}
                      className="snippet-close-btn absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue transition-colors"
                      aria-label="Close venue preview"
                      type="button"
                    >
                      <XIcon
                        size={16}
                        weight="bold"
                        className="text-fg-neutral-strong"
                      />
                    </button>

                    <button
                      type="button"
                      className="w-full text-left rounded p-0 focus-blue cursor-pointer"
                      aria-label={`View details for ${snippetVenue.name}`}
                    >
                      <VenueCard
                        location={snippetVenue}
                        onOpen={() => handleLoadVenue(snippetVenue)}
                        savedPlaces={[]}
                        truncateText={true}
                        index={0}
                        totalCount={locations.length}
                        showMetaInfo={true}
                        showOpeningHours={true}
                        showVariantIndicators={true}
                        showDescription={true}
                        showAddress={false}
                        inMapView={true}
                      />
                    </button>
                  </div>
                </div>
              </InfoWindow>
            )}
          </VisGlMap>
        </APIProvider>
      )}

      {/* Goto current location, +, - */}
      <div
        className="absolute right-0 bottom-0 top-0 flex flex-col items-end justify-end"
        aria-label="Map controls"
      >
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            const z = mapRef?.getZoom();
            if (mapRef && typeof z === "number" && z < 17) mapRef.setZoom(z + 1);
          }}
          aria-label="Zoom in"
          className="p-2 mb-1 mr-2 shadow-md flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold"
        >
          <FaPlus
            size={24}
            color={isDark ? "white" : "#121042"}
            aria-hidden="true"
          />
        </StandardButton>
        <StandardButton
          variant="icon"
          size="md"
          onClick={() => {
            const z = mapRef?.getZoom();
            if (mapRef && typeof z === "number" && z > 0) mapRef.setZoom(z - 1);
          }}
          aria-label="Zoom out"
          className="p-2 mb-8 mr-2 shadow-md flex items-center justify-center bg-container-brand-minimal rounded-[8px] font-semibold"
        >
          <FaMinus
            size={24}
            color={isDark ? "white" : "#121042"}
            aria-hidden="true"
          />
        </StandardButton>
      </div>

      {/* Mobile: fixed bottom snippet (below md) */}
      {!selectedVenue &&
        activeMarker &&
        (() => {
          const location = locations.find((l) => l.id === activeMarker);
          const index = locations.findIndex((l) => l.id === activeMarker);
          if (!location) return null;

          return (
            <div className="md:hidden fixed bottom-0 left-0 right-0 z-[1000] p-3 pb-4 animate-slide-up pointer-events-none">
              <style>{`
                @keyframes slide-up {
                  from { transform: translateY(100%); }
                  to { transform: translateY(0); }
                }
                .animate-slide-up { animation: slide-up 0.3s ease-out; }
                .mobile-snippet-wrapper button.bg-opacity-30 { right: 52px !important; }
                .mobile-snippet-container { max-height: 280px; overflow: hidden; }
                .mobile-snippet-wrapper figure {
                  aspect-ratio: 16 / 7 !important;
                  max-height: 140px !important;
                }
                .mobile-snippet-wrapper figure img { object-fit: cover !important; }`}</style>

              <div className="mobile-snippet-container relative">
                {/* Close button - positioned at right edge */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseSnippet();
                  }}
                  className="snippet-close-btn absolute top-2 right-2 z-20 w-8 h-8 flex items-center justify-center bg-bg-brand-secondary-default border border-border-token-brand-minimal rounded-full hover:bg-bg-brand-ghost-hover focus-blue transition-colors pointer-events-auto"
                  aria-label="Close venue preview"
                  type="button"
                >
                  <XIcon
                    size={16}
                    weight="bold"
                    className="text-fg-neutral-strong"
                  />
                </button>

                <button
                  type="button"
                  className="w-full text-left rounded p-0 focus-blue mobile-snippet-wrapper cursor-pointer pointer-events-auto"
                  aria-label={`View details for ${location.name}`}
                >
                  <VenueCard
                    location={location}
                    onOpen={() => handleLoadVenue(location)}
                    savedPlaces={[]}
                    truncateText={true}
                    index={index}
                    totalCount={locations.length}
                    showMetaInfo={true}
                    showOpeningHours={true}
                    showVariantIndicators={true}
                    showDescription={true}
                    showAddress={false}
                    inMapView={true}
                  />
                </button>
              </div>
            </div>
          );
        })()}
    </div>
  );
}

export default MapViewForNearby;
